import React, { useState, useEffect } from 'react';
import T from '../../components/common/T';
import api from '../../api/axiosConfig';
import Price from "../../components/common/Price";
import { XMarkIcon, DocumentTextIcon, BanknotesIcon, ClockIcon } from "@heroicons/react/24/outline";
import RecordBillPaymentModal from './RecordBillPaymentModal'; 

export default function VendorBillDetailsModal({ bill: initialBill, onClose }) {
  const [bill, setBill] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);

  // 1. Fetch Fresh Data (with Payment History)
  const fetchDetails = async () => {
      if (!initialBill?.id) return;
      try {
          const res = await api.get(`/accounting/bills/${initialBill.id}`);
          setBill(res.data);
      } catch (err) {
          console.error("Failed to load details", err);
      }
  };

  useEffect(() => {
      if (initialBill) fetchDetails();
  }, [initialBill]);

  // Prevent rendering if data isn't loaded yet
  if (!bill) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm p-4 animate-fade-in">
      
      {/* Centered Modal Box */}
      <div className="bg-white dark:bg-[#161b22] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-200 dark:border-[#30363d] transition-colors">
        
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50 dark:bg-[#21262d]">
            <div className="flex items-center gap-3">
                <div className="bg-white dark:bg-[#161b22] p-2 rounded-lg shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <DocumentTextIcon className="w-6 h-6 text-red-500" />
                </div>
                <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">{bill.bill_number}</h2>
                    <p className="text-sm text-gray-500 dark:text-slate-400">{bill.vendor_name}</p>
                </div>
            </div>
            <button onClick={onClose} className="bg-white dark:bg-[#21262d] p-2 rounded-full shadow-sm border border-gray-200 dark:border-[#30363d] text-gray-400 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 transition">
                <XMarkIcon className="w-5 h-5" />
            </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Status & Balance Card */}
            <div className="flex justify-between items-center bg-white dark:bg-[#21262d] p-5 rounded-xl border border-gray-100 dark:border-[#30363d] shadow-sm">
                <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-bold tracking-wider"><T>Balance Due</T></p>
                    <p className={`text-xl font-black mt-1 ${Number(bill.balance_due) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        <Price amount={Number(bill.balance_due)} />
                    </p>
                </div>
                <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase border ${
                    bill.status === 'Paid' ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800' :
                    bill.status === 'Posted' ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                    'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700'
                }`}>
                    {bill.status}
                </span>
            </div>

            {/* Bill Details Grid */}
            <div className="bg-gray-50 dark:bg-[#161b22] rounded-xl p-5 border border-gray-100 dark:border-[#30363d]">
                <h3 className="font-bold text-gray-900 dark:text-white uppercase text-xs tracking-wide mb-4 border-b border-gray-200 dark:border-[#30363d] pb-2"><T>Bill Information</T></h3>
                <div className="grid grid-cols-2 gap-6 text-sm">
                    <div>
                        <span className="block text-gray-500 dark:text-slate-400 text-xs uppercase mb-1"><T>Bill Date</T></span>
                        <span className="font-bold text-gray-900 dark:text-white">{new Date(bill.bill_date).toLocaleDateString()}</span>
                    </div>
                    <div>
                        <span className="block text-gray-500 dark:text-slate-400 text-xs uppercase mb-1"><T>Due Date</T></span>
                        <span className="font-bold text-red-600 dark:text-red-400">{new Date(bill.due_date).toLocaleDateString()}</span>
                    </div>
                    <div>
                        <span className="block text-gray-500 dark:text-slate-400 text-xs uppercase mb-1"><T>Project</T></span>
                        <span className="font-bold text-indigo-600 dark:text-indigo-400">{bill.project_name || 'General'}</span>
                    </div>
                    <div>
                        <span className="block text-gray-500 dark:text-slate-400 text-xs uppercase mb-1"><T>Total Amount</T></span>
                        <span className="font-bold text-gray-900 dark:text-white"><Price amount={Number(bill.total_amount)} /></span>
                    </div>
                </div>
                {bill.notes && (
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-[#30363d]">
                         <span className="block text-xs text-gray-400 dark:text-slate-500 uppercase mb-1"><T>Notes</T></span>
                         <p className="text-gray-600 dark:text-slate-300 italic">"{bill.notes}"</p>
                    </div>
                )}
            </div>

            {/* Payment History */}
            <div>
                <h3 className="font-bold text-gray-900 dark:text-white uppercase text-xs tracking-wide mb-3 flex items-center gap-2">
                    <ClockIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" /> Payment History
                </h3>
                
                {(!bill.payments || bill.payments.length === 0) ? (
                    <div className="text-sm text-gray-400 dark:text-slate-500 italic text-center py-6 bg-gray-50 dark:bg-[#161b22] rounded-lg border border-dashed border-gray-200 dark:border-[#30363d]">
                        No payments recorded yet.
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {bill.payments.map((pay, i) => (
                            <li key={i} className="bg-green-50 dark:bg-green-950/20 p-4 rounded-xl border border-green-100 dark:border-green-900/40 flex justify-between items-center">
                                <div>
                                    <p className="font-bold text-gray-800 dark:text-green-300 text-lg"><Price amount={Number(pay.amount)} /></p>
                                    <p className="text-xs text-green-700 dark:text-green-400 opacity-80 mt-0.5 font-medium">
                                        {new Date(pay.payment_date).toLocaleDateString()} • {pay.payment_method.replace('_', ' ')}
                                    </p>
                                </div>
                                {pay.reference && (
                                    <span className="text-xs bg-white dark:bg-[#21262d] border border-green-200 dark:border-green-800 px-3 py-1 rounded-md text-green-700 dark:text-green-400 font-mono font-bold shadow-sm">
                                        #{pay.reference}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 dark:border-[#30363d] bg-gray-50 dark:bg-[#21262d] flex justify-end gap-3">
            <button onClick={onClose} className="px-5 py-2.5 text-sm font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition">
                Close
            </button>
            {bill.status !== 'Paid' && (
                <button 
                    onClick={() => setShowPayModal(true)}
                    className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-md transition transform hover:scale-105"
                >
                    <BanknotesIcon className="w-4 h-4" />
                    Record Payment
                </button>
            )}
        </div>

      </div>

      {/* Payment Modal */}
      {showPayModal && (
        <RecordBillPaymentModal 
            isOpen={showPayModal}
            onClose={() => setShowPayModal(false)}
            bill={bill}
            onSuccess={fetchDetails} 
        />
      )}
    </div>
  );
}